import * as React from "react";
import { observer } from "mobx-react";
import { action, computed, makeObservable, observable } from "mobx";
import {
    ComboBox,
    type IComboBoxOption,
    type IComboBoxProps,
    type ISelectableOption,
} from "@fluentui/react";
import type { WindowsTimezone, TimezoneListEntry } from "../../api";
import { LoadingState } from "../../utils/loading-state";
import { type UUID } from "../../utils/uuid";

export type FormUpdateDefaultTimezoneProps = Omit<
    IComboBoxProps,
    "options" | "selectedKey" | "onChange"
>;

enum UpdateDefaultTimezoneFeatures {
    CHANGE_DEFAULT_TIMEZONE = "change default timezone",
}

import { store } from "../../store";

@observer
export class FormUpdateDefaultTimezone extends React.Component<FormUpdateDefaultTimezoneProps> {
    @observable private accessor timezoneList: TimezoneListEntry[] = [];
    private readonly loadingFeatures = new LoadingState<UpdateDefaultTimezoneFeatures>();

    constructor(props: FormUpdateDefaultTimezoneProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.publicSettingsApi.publicSettingsListTimezones({}).then((timezoneList) => {
            this.timezoneList = timezoneList;
        });
    }

    @computed private get options(): ISelectableOption<unknown>[] {
        return this.timezoneList.map((timezone) => ({
            key: timezone.timezone,
            text: timezone.description,
        }));
    }

    @action.bound private async updateDefaultTimeZone(
        publicSettingId: UUID,
        option: IComboBoxOption | undefined,
    ): Promise<void> {
        const timezone = option?.key as WindowsTimezone;
        await this.loadingFeatures.wrap(
            UpdateDefaultTimezoneFeatures.CHANGE_DEFAULT_TIMEZONE,
            store.repositoryPublicSettings.update(publicSettingId, {
                defaultTimezone: timezone,
            }),
        );
    }

    public render(): JSX.Element {
        return (
            <ComboBox
                {...this.props}
                disabled={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultTimezoneFeatures.CHANGE_DEFAULT_TIMEZONE,
                    ) || this.props.disabled
                }
                label={store.i18n.t("formUpdateDefaultTimezone.label")}
                options={this.options}
                selectedKey={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultTimezoneFeatures.CHANGE_DEFAULT_TIMEZONE,
                    )
                        ? undefined
                        : store.servicePublicSettings.publicSetting?.defaultTimezone
                }
                onChange={(_, option) => {
                    if (store.servicePublicSettings.publicSetting) {
                        this.updateDefaultTimeZone(
                            store.servicePublicSettings.publicSetting.id,
                            option,
                        );
                    }
                }}
            />
        );
    }
}
